import { Box, Tab, formatMinutes, pn, Skeleton, Stack } from "@compass/core";
import Tabs from "@mui/material/Tabs";
import { HomeServiceDashboardRow } from "generated/openApi";
import { DevExpressReport } from "components";
import { Column } from "devextreme-react/data-grid";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useServiceData } from "../contexts";
import { DashboardCard } from "../components/DashboardCard";
import { ServiceFilterControls } from "./ServiceFilterControls";
import { ServicePeriod } from "../types";

const ServiceTable = (props: { rows: HomeServiceDashboardRow[] | undefined; loading: boolean }) => {
    const appTenantName = useSelector((state: RootState) => state.app.tenantName);
    
    return props.loading ? (
        <Skeleton variant="rounded" height={500} />
    ) : (
        <DevExpressReport
            dataSource={props.rows}
            fullPageSize
            showColumnChooser={false}
        >
            <Column dataField={pn<HomeServiceDashboardRow>("serviceType")} caption="Category" />
            <Column dataField={pn<HomeServiceDashboardRow>("totalStudents")} caption="Students" />
            <Column
                dataField={pn<HomeServiceDashboardRow>("totalDurationInMinutes")}
                caption="Total Duration"
                cellRender={(data) => formatMinutes(Number(data.text))}
            />
        </DevExpressReport>
    );
};

const ServiceTableCardContent = () => {
    const { currentRows, loading, selectedPeriod, updateSelectedPeriod } = useServiceData();
    
    const handleTabChange = (_event: React.SyntheticEvent, newValue: ServicePeriod) => {
        updateSelectedPeriod(newValue);
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                gap={2}
                sx={{ mb: 2 }}
            >
                <Tabs value={selectedPeriod} onChange={handleTabChange}>
                    <Tab label="This Month" value={ServicePeriod.ThisMonth} />
                    <Tab label="Previous Month" value={ServicePeriod.PreviousMonth} />
                </Tabs>
                <Box sx={{
                    flexGrow: 1,
                    minWidth: 250,
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                }}>
                    <ServiceFilterControls />
                </Box>
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <ServiceTable rows={currentRows} loading={loading} />
            </Box>
        </Box>
    );
};

export const ServiceTableCard = () => {
    const { currentRows, selectedPeriod } = useServiceData();
    
    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (!currentRows || currentRows.length === 0) return;

        const periodLabel = selectedPeriod === ServicePeriod.ThisMonth ? "This Month" : "Previous Month";
        const exportDate = new Date().toLocaleDateString();
        
        // CSV Headers with metadata
        const metadata = [
            '# Monthly Services',
            `# Period: ${periodLabel}`,
            `# Exported: ${exportDate}`,
            '', // blank line
        ];
        
        const headers = ['Category', 'Students', 'Total Duration'];
        
        const rows = currentRows.map(item => [
            `"${item.serviceType}"`,
            item.totalStudents,
            `"${formatMinutes(item.totalDurationInMinutes)}"`
        ]);

        const csvContent = [
            ...metadata,
            headers.join(','), 
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Link to trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filenamePeriod = selectedPeriod === ServicePeriod.ThisMonth ? "ThisMonth" : "PreviousMonth";
        link.setAttribute('download', `Services_${filenamePeriod}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <DashboardCard title="Monthly Services" onExport={handleExport}>
            <ServiceTableCardContent />
        </DashboardCard>
    );
};
